import { useEffect, useMemo, useState, type FC } from "react";
import type { Dayjs } from "dayjs";
import { DownloadOutlined } from "@ant-design/icons";

import {
  Button,
  Input,
  Pagination,
  PillTabs,
  RebetLoader,
  Select,
  type PillTabsItem,
} from "../../shared";
import DataTable, {
  type DataTableProps,
} from "../../shared/dashboards/DataTable/DataTable";
import DatePicker from "../../shared/DatePicker/DatePicker";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { useDebouncedValue } from "../../../hooks/useDebouncedSearchQuery";

import { usePortalMe } from "../../../queries/affiliate-portal/usePortalMe";
import { usePortalView } from "../../../queries/affiliate-portal/usePortalView";
import { usePortalKpiCards } from "../../../queries/affiliates/useAffiliateKpiCards";
import { usePortalViewReportDownload } from "../../../queries/affiliates/useAffiliateReportDownloads";
import { clearAffiliatePortalAndRedirect } from "../../../utils/tokenUtils";
import { mapPortalViewConfigFromResponse } from "../../../queries/affiliates/affiliateMappers";
import AffiliateKpisOverview, {
  type AffiliateKpisMetricId,
} from "../../Affiliates/AffiliateKpis/AffiliateKpisOverview";
import AffiliateUsers from "../../Affiliates/AffiliateProfile/AffiliateUsers/AffiliateUsers";
import { KPI_ELIGIBLE_IDS } from "../../Affiliates/NewAffiliate/AffiliatePamAccount/affiliatePamAccountData";
import type {
  KpiEligibleId,
  ShareableColumnId,
} from "../../Affiliates/NewAffiliate/AffiliatePamAccount/affiliatePamAccountData";
import {
  LinkStatusBadge,
  getVisiblePromoCodeColumns,
} from "../../Affiliates/sharedPromoCodeColumns";
import useCurrencySymbol from "../../../hooks/useCurrencySymbol";
import type { PromoCodeLinkStatus } from "../../../types/affiliates";
import { portalCodeToRow, type PortalCodeTableRow } from "./portalCodeColumns";

import styles from "./AffiliatePortalDashboard.module.css";
import { getAdminNow } from "../../../utils/dateService";

type DateRange = [Dayjs, Dayjs];

type PortalTab = "kpis" | "promoCodes" | "users";

// No Affiliate Info tab — contract / plan / lifecycle data and actions are
// operator-only and never reach the portal.
const PORTAL_TABS: ReadonlyArray<PillTabsItem<PortalTab>> = [
  {
    id: "kpis",
    label: AppStrings.KPIS,
    elementId: "affiliate-portal-tab-kpis",
    ariaControls: "affiliate-portal-panel-kpis",
  },
  {
    id: "promoCodes",
    label: AppStrings.PROMO_CODES,
    elementId: "affiliate-portal-tab-promoCodes",
    ariaControls: "affiliate-portal-panel-promoCodes",
  },
  {
    id: "users",
    label: AppStrings.USERS,
    elementId: "affiliate-portal-tab-users",
    ariaControls: "affiliate-portal-panel-users",
  },
];

type DatePickerProps = {
  dates: DateRange;
  setDates: (dates: DateRange) => void;
  getData: (query?: string | null) => void;
  showPresets?: boolean;
  startRange?: string;
};
const TypedDatePicker = DatePicker as FC<DatePickerProps>;

// Widen the literal tuple so `.includes(id)` accepts any ShareableColumnId.
const KPI_IDS: ReadonlyArray<ShareableColumnId> = KPI_ELIGIBLE_IDS;

// Everything the operator toggled on renders — what data actually arrives
// is the BE's access-mode call (RESTRICTED strips financials server-side,
// so those tiles read 0). `avgLtv` is FE-derived from Total Revenue, so it
// rides along only while `totalRevenue` is selected — same gate the Edit
// Portal View preview applies.
const selectionToVisibleMetrics = (
  selection: ReadonlyArray<ShareableColumnId>,
): ReadonlyArray<AffiliateKpisMetricId> => {
  const metrics: KpiEligibleId[] = selection.filter((id): id is KpiEligibleId =>
    KPI_IDS.includes(id),
  );
  return selection.includes("totalRevenue")
    ? [...metrics, "avgLtv"]
    : metrics;
};

/**
 * Affiliate-facing portal: PillTabs (Kpis / Promo Codes / Users), KPI
 * overview, codes table, and Users table.
 */
const AffiliatePortalDashboard: FC = () => {
  const meQuery = usePortalMe();
  const profile = meQuery.data?.profile;

  // Affiliates who get deactivated mid-session are bounced out.
  useEffect(() => {
    if (profile?.status === "deactivated") {
      clearAffiliatePortalAndRedirect();
    }
  }, [profile?.status]);

  const [dates, setDates] = useState<DateRange>([
    getAdminNow().startOf("month"),
    getAdminNow(),
  ]);
  const [activeTab, setActiveTab] = useState<PortalTab>("kpis");

  const viewQuery = usePortalView({
    start_date: dates[0].format("YYYY-MM-DD"),
    end_date: dates[1].format("YYYY-MM-DD"),
  });
  const view = viewQuery.data;

  // PAMENG-2107 moved reporting values out of /view — KPI tiles now come
  // from the portal analytics endpoint (financials already stripped BE-side).
  const kpisQuery = usePortalKpiCards({
    start_date: dates[0].format("YYYY-MM-DD"),
    end_date: dates[1].format("YYYY-MM-DD"),
  });

  const [searchValue, setSearchValue] = useState("");
  const [linkStatusFilter, setLinkStatusFilter] = useState<
    PromoCodeLinkStatus | undefined
  >(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const debouncedSearch = useDebouncedValue(searchValue);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, linkStatusFilter, pageSize]);

  const selection = useMemo(
    () => (view ? mapPortalViewConfigFromResponse(view.view_config) : []),
    [view],
  );

  const visibleMetrics = useMemo(
    () => selectionToVisibleMetrics(selection),
    [selection],
  );

  // Operator shared no KPI cards → the Kpis tab has nothing legitimate to
  // render (tiles and charts would both surface unshared metrics), so the
  // tab itself disappears and the portal lands on Promo Codes.
  const tabs = useMemo(
    () =>
      visibleMetrics.length > 0
        ? PORTAL_TABS
        : PORTAL_TABS.filter((tab) => tab.id !== "kpis"),
    [visibleMetrics],
  );
  const effectiveTab = tabs.some((tab) => tab.id === activeTab)
    ? activeTab
    : "promoCodes";

  const currencySymbol = useCurrencySymbol() ?? "$";
  // Same shared column set the operator table uses (always-on Reason + App
  // Link included), so the two tables stay visually identical.
  const codesColumns = useMemo(
    () => getVisiblePromoCodeColumns(selection, currencySymbol),
    [selection, currencySymbol],
  );

  const allRows = useMemo<PortalCodeTableRow[]>(
    () => (view?.codes ?? []).map(portalCodeToRow),
    [view?.codes],
  );

  // Feeds the charts' own All Codes selects.
  const codeOptions = useMemo(
    () =>
      (view?.codes ?? []).map((code) => ({
        value: code.referral_code,
        label: code.referral_code,
      })),
    [view?.codes],
  );

  const rows = useMemo<PortalCodeTableRow[]>(() => {
    const normalizedSearch = debouncedSearch.trim().toLowerCase();
    return allRows.filter((row) => {
      if (linkStatusFilter && row.linkStatus !== linkStatusFilter) return false;
      if (!normalizedSearch) return true;
      return (row.referralCode ?? "").toLowerCase().includes(normalizedSearch);
    });
  }, [allRows, debouncedSearch, linkStatusFilter]);

  const pageRows = useMemo<PortalCodeTableRow[]>(
    () => rows.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [rows, currentPage, pageSize],
  );

  // Server-side CSV export — BE builds the config-gated report scoped to the
  // selected date range and returns a signed URL.
  const portalDownload = usePortalViewReportDownload();
  const handleDownload = () =>
    portalDownload.mutate({
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
    });

  const displayName =
    profile?.contact_name || profile?.company_name || profile?.email || "";

  if (meQuery.isLoading || viewQuery.isLoading) {
    return (
      <div
        className={styles.loading}
        data-testid="affiliate-portal-dashboard-loading"
      >
        <RebetLoader height={"32px"} width={"32px"} />
      </div>
    );
  }

  if (meQuery.error || viewQuery.error || !view) {
    return (
      <div
        className={styles.error}
        data-testid="affiliate-portal-dashboard-error"
      >
        {AppStrings.COULDNT_LOAD_YOUR_DATA_TRY_SIGNING_IN_AGAIN}
      </div>
    );
  }

  return (
    <div className={styles.container} data-testid="affiliate-portal-dashboard">
      <div className={styles.headerRow}>
        <h1 className={styles.title}>
          {AppStrings.SHARE_AFFILIATE_PROFILE_LABEL}{" "}
          <span>{displayName}</span>
        </h1>
        <div
          className={styles.datePicker}
          data-testid="affiliate-portal-date-picker"
        >
          <TypedDatePicker
            dates={dates}
            setDates={setDates}
            getData={() => {}}
            showPresets
            startRange={AppStrings.THIS_MONTH}
          />
        </div>
      </div>

      <div className={styles.tabsRow}>
        <PillTabs<PortalTab>
          items={tabs}
          value={effectiveTab}
          onChange={setActiveTab}
          ariaLabel={AppStrings.AFFILIATES}
          data-testid="affiliate-portal-tabs"
        />
      </div>

      <div
        className={styles.tabPanel}
        role="tabpanel"
        id={`affiliate-portal-panel-${effectiveTab}`}
        aria-labelledby={`affiliate-portal-tab-${effectiveTab}`}
        data-testid={`affiliate-portal-panel-${effectiveTab}`}
      >
        {effectiveTab === "kpis" && (
          <AffiliateKpisOverview
            kpis={kpisQuery.data}
            isLoading={kpisQuery.isFetching}
            scope="affiliate"
            visibleMetrics={visibleMetrics}
            dataSource="portal"
            codeOptions={codeOptions}
            isLoadingCodes={viewQuery.isFetching}
          />
        )}

        {effectiveTab === "promoCodes" && (
          <div className={styles.tableSection}>
            <div className={styles.tableHeader}>
              <h2 className={styles.tableTitle}>
                {AppStrings.AFFILIATE_CODES}{" "}
                <span className={styles.tableTitleCount}>({rows.length})</span>
              </h2>
              <Button
                intent="neutral"
                size="large"
                iconLeft={<DownloadOutlined style={{ fontSize: 16 }} />}
                aria-label="Download"
                loading={portalDownload.isPending}
                onClick={handleDownload}
              />
            </div>

            <div className={styles.searchSection}>
              <div className={styles.searchInputs}>
                <Input
                  className={styles.searchInput}
                  placeholder={AppStrings.SEARCH_BY_CODE}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  allowClear
                />
                <Select<PromoCodeLinkStatus>
                  className={styles.statusFilter}
                  classNames={{ popup: { root: "affiliates-status-dropdown" } }}
                  placeholder={AppStrings.STATUS}
                  value={linkStatusFilter}
                  onChange={(value) => setLinkStatusFilter(value)}
                  allowClear
                  options={[
                    {
                      value: "provisioned",
                      label: <LinkStatusBadge status="provisioned" />,
                    },
                    {
                      value: "pending",
                      label: <LinkStatusBadge status="pending" />,
                    },
                    {
                      value: "failed",
                      label: <LinkStatusBadge status="failed" />,
                    },
                    {
                      value: "deleted",
                      label: <LinkStatusBadge status="deleted" />,
                    },
                  ]}
                />
              </div>
            </div>

            <DataTable<PortalCodeTableRow>
              rowKey="key"
              columns={
                codesColumns as DataTableProps<PortalCodeTableRow>["columns"]
              }
              dataSource={pageRows}
              scrollPreset="compact"
              loading={viewQuery.isFetching}
              pagination={false}
            />
            <Pagination
              total={rows.length}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageSizeChange={(value: number) => {
                setCurrentPage(1);
                setPageSize(value);
              }}
              onPageChange={(nextPage: number) => setCurrentPage(nextPage)}
              itemLabel="codes"
            />
          </div>
        )}

        {effectiveTab === "users" && (
          <AffiliateUsers
            dataSource="portal"
            selectedColumns={selection}
            startDate={dates[0].format("YYYY-MM-DD")}
            endDate={dates[1].format("YYYY-MM-DD")}
          />
        )}
      </div>
    </div>
  );
};

export default AffiliatePortalDashboard;
